import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { TruckIcon, CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";

// Pull a human-readable detail out of an axios error (FastAPI sends string or
// validation-array `detail`), matching the B2COrders convention.
const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // "loading" | "success" | "error"
  const [status, setStatus] = useState(token ? "loading" : "error");
  const [message, setMessage] = useState(
    token ? "" : "This verification link is missing its token. Please use the link from your email."
  );

  // Resend affordance state (shown on the error path).
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setStatus("loading");
    axios
      .get(`${API}/auth/verify-email`, { params: { token } })
      .then(({ data }) => {
        if (cancelled) return;
        setMessage(data?.message || "Email verified — you can now log in.");
        setStatus("success");
      })
      .catch((error) => {
        if (cancelled) return;
        setMessage(errDetail(error, "Invalid or expired verification link"));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleResend = useCallback(
    async (e) => {
      e.preventDefault();
      const email = resendEmail.trim();
      if (!email) {
        toast.error("Enter the email you signed up with");
        return;
      }
      setResending(true);
      try {
        const { data } = await axios.post(`${API}/auth/resend-verification`, { email });
        toast.success(
          data?.message || "If that account exists and is unverified, a new link has been sent."
        );
      } catch (error) {
        toast.error(errDetail(error, "Could not resend the verification email. Please try again."));
      } finally {
        setResending(false);
      }
    },
    [resendEmail]
  );

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          {/* Compact brand header */}
          <div className="mb-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-3">
              <div className="p-2.5 rounded-xl bg-brand">
                <TruckIcon className="w-7 h-7 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-bold text-slate-800 leading-none">EdgeLMS</h1>
                <p className="text-xs font-medium text-slate-500 tracking-wide">by Edgistify</p>
              </div>
            </div>
          </div>

          <Card className="shadow-2xl border-0">
            <CardContent className="py-10 text-center space-y-4">
              {status === "loading" && (
                <>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-bg">
                    <Loader2 className="h-7 w-7 text-brand animate-spin" />
                  </div>
                  <CardTitle className="text-xl font-semibold">Verifying your email…</CardTitle>
                  <p className="text-sm text-slate-600">This will only take a moment.</p>
                </>
              )}

              {status === "success" && (
                <>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-bg">
                    <CheckCircle2 className="h-7 w-7 text-brand" />
                  </div>
                  <CardTitle className="text-xl font-semibold">Email verified</CardTitle>
                  <p className="text-sm text-slate-600 px-2 leading-relaxed">{message}</p>
                  <Link
                    to="/login"
                    data-testid="verify-go-to-login"
                    className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-4 text-base font-medium text-white transition-colors hover:bg-brand-dark"
                  >
                    Go to login
                  </Link>
                </>
              )}

              {status === "error" && (
                <>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                    <XCircle className="h-7 w-7 text-destructive" />
                  </div>
                  <CardTitle className="text-xl font-semibold">Verification failed</CardTitle>
                  <p className="text-sm text-slate-600 px-2 leading-relaxed">{message}</p>

                  <form onSubmit={handleResend} className="space-y-3 pt-2 text-left" noValidate>
                    <div className="space-y-2">
                      <Label htmlFor="resend-email">Resend the verification link</Label>
                      <Input
                        id="resend-email"
                        data-testid="verify-resend-email-input"
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        className="text-base h-11"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="verify-resend-button"
                      variant="outline"
                      className="w-full h-11"
                      disabled={resending}
                    >
                      {resending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Resend verification email
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-sm text-slate-600">
                    <Link
                      to="/login"
                      data-testid="verify-back-to-login"
                      className="font-medium text-brand hover:underline"
                    >
                      Back to login
                    </Link>
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <footer className="py-4 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} OptiSupply Chain Solution Pvt. Ltd. · EdgeLMS
      </footer>
    </div>
  );
};

export default VerifyEmailPage;
